import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, Table, InputGroup, Form, Spinner, Alert, Modal } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import MasterLayout from '../../masterLayout/MasterLayout';
import AdminPageHeader from '../../components/common/AdminPageHeader';
import { Drawer } from '../../theme/components';
import { API_URL } from '../../config/api';
import './AdminTicketsPage.css';
import styles from './AdminTicketsPage.module.scss';

const AdminTicketsPage = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showDetailsDrawer, setShowDetailsDrawer] = useState(false);
  const [ticketDetails, setTicketDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);

  const statusLabels = {
    open: { label: 'Aberto', color: 'warning', icon: 'mdi:clock-outline' },
    in_progress: { label: 'Respondido', color: 'info', icon: 'mdi:reply' },
    waiting_user: { label: 'Aguardando Resposta', color: 'secondary', icon: 'mdi:account-clock' },
    resolved: { label: 'Resolvido', color: 'success', icon: 'mdi:check-circle' },
    closed: { label: 'Fechado', color: 'dark', icon: 'mdi:close-circle' }
  };

  const categoryLabels = {
    technical: { label: 'Técnico', icon: 'mdi:wrench', color: '#3B82F6' },
    financial: { label: 'Financeiro', icon: 'mdi:currency-usd', color: '#10B981' },
    administrative: { label: 'Administrativo', icon: 'mdi:account-supervisor', color: '#8B5CF6' },
    general: { label: 'Geral', icon: 'mdi:help-circle', color: '#6B7280' }
  };

  const priorityLabels = {
    low: { label: 'Baixa', color: '#10B981' },
    normal: { label: 'Normal', color: '#3B82F6' },
    high: { label: 'Alta', color: '#F59E0B' },
    urgent: { label: 'Urgente', color: '#EF4444' }
  };

  useEffect(() => {
    loadTickets();
  }, []);

  const loadTickets = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/tickets/admin/all`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erro ao carregar tickets');
      }

      setTickets(data.tickets || []);
    } catch (err) {
      console.error('Erro ao carregar tickets:', err);
      setError(err.message || 'Erro ao carregar tickets');
    } finally {
      setLoading(false);
    }
  };

  const loadTicketDetails = async (ticket) => {
    setSelectedTicket(ticket);
    setShowDetailsDrawer(true);
    setLoadingDetails(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/tickets/${ticket.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erro ao carregar detalhes');
      }

      console.log('🎫 Dados do ticket recebidos:', data.ticket);
      setTicketDetails(data.ticket);
    } catch (err) {
      console.error('Erro ao carregar detalhes:', err);
      setError(err.message);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();

    if (!replyMessage.trim()) {
      setError('Por favor, digite uma mensagem');
      return;
    }

    setSending(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/tickets/${selectedTicket.id}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ message: replyMessage })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erro ao enviar resposta');
      }

      setSuccess('Resposta enviada com sucesso!');
      setReplyMessage('');
      
      // Recarregar detalhes
      await loadTicketDetails(selectedTicket);
      await loadTickets();
    } catch (err) {
      console.error('Erro ao enviar resposta:', err);
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const handleUpdateStatus = async (ticketId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/tickets/${ticketId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erro ao atualizar status');
      }

      setSuccess('Status atualizado com sucesso!');
      await loadTickets();
      
      if (showDetailsDrawer) {
        await loadTicketDetails(selectedTicket);
      }
    } catch (err) {
      console.error('Erro ao atualizar status:', err);
      setError(err.message);
    }
  };

  const getFilteredTickets = () => {
    let filtered = tickets;

    if (filterStatus !== 'all') {
      filtered = filtered.filter(t => t.status === filterStatus);
    }

    if (searchTerm) {
      filtered = filtered.filter(t =>
        t.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.id.toString().includes(searchTerm)
      );
    }

    return filtered;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusCount = (status) => {
    if (status === 'all') return tickets.length;
    return tickets.filter(t => t.status === status).length;
  };

  const filteredTickets = getFilteredTickets();

  return (
    <MasterLayout>
      <div className="admin-tickets-page">
        <div className="dashboard-main-body">
          <div className="d-flex justify-content-between align-items-start">
            <AdminPageHeader
              icon="solar:headphones-round-sound-bold"
              title="Helpdesk"
              subtitle="Gerenciamento de tickets de suporte"
            />
            <Button
              variant="outline-primary"
              onClick={loadTickets}
              disabled={loading}
              style={{ marginTop: '8px' }}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Carregando...
                </>
              ) : (
                <>
                  <Icon icon="mdi:refresh" className="me-1" />
                  Atualizar
                </>
              )}
            </Button>
          </div>

        <Card className="bg-transparent border-0">
          <Card.Header className="d-none"></Card.Header>

          <Card.Body>
            {error && (
              <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-4">
                <Icon icon="mdi:alert-circle" className="me-2" />
                {error}
              </Alert>
            )}

            {success && (
              <Alert variant="success" dismissible onClose={() => setSuccess('')} className="mb-4">
                <Icon icon="mdi:check-circle" className="me-2" />
                {success}
              </Alert>
            )}

            {/* Botões de Filtro */}
            <div className="d-flex gap-2 mb-4 flex-wrap">
              <Button
                variant={filterStatus === 'all' ? 'primary' : 'outline-primary'}
                onClick={() => setFilterStatus('all')}
                className="d-flex align-items-center gap-2"
              >
                <Icon icon="mdi:all-inclusive" />
                Todos
                {getStatusCount('all') > 0 && (
                  <Badge bg="light" text="dark">{getStatusCount('all')}</Badge>
                )}
              </Button>
              <Button
                variant={filterStatus === 'open' ? 'warning' : 'outline-warning'}
                onClick={() => setFilterStatus('open')}
                className="d-flex align-items-center gap-2"
              >
                <Icon icon="mdi:clock-outline" />
                Abertos
                {getStatusCount('open') > 0 && (
                  <Badge bg="light" text="dark">{getStatusCount('open')}</Badge>
                )}
              </Button>
              <Button
                variant={filterStatus === 'in_progress' ? 'info' : 'outline-info'}
                onClick={() => setFilterStatus('in_progress')}
                className="d-flex align-items-center gap-2"
              >
                <Icon icon="mdi:progress-clock" />
                Em Andamento
                {getStatusCount('in_progress') > 0 && (
                  <Badge bg="light" text="dark">{getStatusCount('in_progress')}</Badge>
                )}
              </Button>
              <Button
                variant={filterStatus === 'resolved' ? 'success' : 'outline-success'}
                onClick={() => setFilterStatus('resolved')}
                className="d-flex align-items-center gap-2"
              >
                <Icon icon="mdi:check-circle" />
                Resolvidos
                {getStatusCount('resolved') > 0 && (
                  <Badge bg="light" text="dark">{getStatusCount('resolved')}</Badge>
                )}
              </Button>
            </div>

            {/* Busca */}
            <div className="mb-4">
              <InputGroup>
                <InputGroup.Text>
                  <Icon icon="mdi:magnify" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Buscar por assunto, usuário, email ou ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </div>

            {/* Tabela de Tickets */}
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="text-muted mt-3">Carregando tickets...</p>
              </div>
            ) : filteredTickets.length === 0 ? (
              <div className="text-center py-5">
                <Icon icon="mdi:inbox-outline" size={64} className="text-muted mb-3" />
                <h6 className="text-muted">Nenhum ticket encontrado</h6>
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Usuário</th>
                      <th>Categoria</th>
                      <th>Assunto</th>
                      <th>Prioridade</th>
                      <th>Status</th>
                      <th>Mensagens</th>
                      <th>Criado em</th>
                      <th className="text-center">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTickets.map((ticket) => (
                      <tr key={ticket.id}>
                        <td className="fw-bold">#{ticket.id}</td>
                        <td>
                          <div>
                            <div className="fw-semibold">{ticket.user_name}</div>
                            <small className="text-muted">{ticket.user_email}</small>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <Icon
                              icon={categoryLabels[ticket.category]?.icon}
                              size={20}
                              style={{ color: categoryLabels[ticket.category]?.color }}
                            />
                            <small>{categoryLabels[ticket.category]?.label}</small>
                          </div>
                        </td>
                        <td>
                          <div className="text-truncate" style={{ maxWidth: '250px' }}>
                            {ticket.subject}
                          </div>
                        </td>
                        <td>
                          <Badge
                            style={{
                              backgroundColor: priorityLabels[ticket.priority]?.color,
                              color: 'white'
                            }}
                          >
                            {priorityLabels[ticket.priority]?.label}
                          </Badge>
                        </td>
                        <td>
                          <Badge bg={statusLabels[ticket.status]?.color}>
                            <Icon icon={statusLabels[ticket.status]?.icon} className="me-1" />
                            {statusLabels[ticket.status]?.label}
                          </Badge>
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-1">
                            <Icon icon="mdi:message-text" size={18} className="text-muted" />
                            <span>{ticket.message_count || 0}</span>
                          </div>
                        </td>
                        <td>
                          <small className="text-muted">
                            {formatDate(ticket.created_at)}
                          </small>
                        </td>
                        <td className="text-center">
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => loadTicketDetails(ticket)}
                            title="Ver detalhes e responder"
                          >
                            <Icon icon="mdi:eye" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Drawer de Detalhes */}
        <Drawer
          show={showDetailsDrawer}
          onHide={() => {
            setShowDetailsDrawer(false);
            setSelectedTicket(null);
            setTicketDetails(null);
            setReplyMessage('');
          }}
          title={`Ticket #${selectedTicket?.id || ''}`}
          subtitle="Detalhes e histórico de conversas"
          icon="mdi:ticket"
          size="lg"
          footer={
            ticketDetails && ticketDetails.status !== 'closed' && ticketDetails.status !== 'resolved' ? (
              <>
                <Drawer.Button
                  variant="success"
                  onClick={() => handleUpdateStatus(ticketDetails.id, 'resolved')}
                  icon="mdi:check-circle"
                >
                  Marcar como Resolvido
                </Drawer.Button>
                <Drawer.Button
                  variant="danger"
                  onClick={() => handleUpdateStatus(ticketDetails.id, 'closed')}
                  icon="mdi:close-circle"
                >
                  Fechar Ticket
                </Drawer.Button>
              </>
            ) : (
              <Drawer.Button
                variant="secondary"
                onClick={() => setShowDetailsDrawer(false)}
              >
                Fechar
              </Drawer.Button>
            )
          }
        >
          {loadingDetails ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="text-muted mt-3">Carregando detalhes...</p>
            </div>
          ) : ticketDetails ? (
            <>
              {/* Informações do Ticket */}
              <Drawer.Section title="Informações do Ticket" icon="mdi:information">
                <div className="mb-2">
                  <div className={`d-flex justify-content-between align-items-center ${styles.infoBox}`}>
                    <span className={styles.infoLabel}>Usuário:</span>
                    <span className={styles.infoValue}>{ticketDetails.user_name || 'N/A'}</span>
                  </div>
                  <div className={`d-flex justify-content-between align-items-center ${styles.infoBox}`}>
                    <span className={styles.infoLabel}>Email:</span>
                    <span className={styles.infoValue}>{ticketDetails.user_email || 'N/A'}</span>
                  </div>
                  <div className={`d-flex justify-content-between align-items-center ${styles.infoBox}`}>
                    <span className={styles.infoLabel}>Telefone:</span>
                    <span className={styles.infoValue}>{ticketDetails.user_phone || 'N/A'}</span>
                  </div>
                  <div className={`d-flex justify-content-between align-items-center ${styles.infoBox}`}>
                    <span className={styles.infoLabel}>Categoria:</span>
                    <span className={`${styles.infoValue} d-flex align-items-center`}>
                      <Icon
                        icon={categoryLabels[ticketDetails.category]?.icon}
                        className="me-1"
                        style={{ color: categoryLabels[ticketDetails.category]?.color, fontSize: '14px' }}
                      />
                      {categoryLabels[ticketDetails.category]?.label}
                    </span>
                  </div>
                  <div className={`d-flex justify-content-between align-items-center ${styles.infoBox}`}>
                    <span className={styles.infoLabel}>Prioridade:</span>
                    <Badge
                      className="px-2 py-1 mx-2"
                      style={{
                        backgroundColor: priorityLabels[ticketDetails.priority]?.color,
                        color: 'white',
                        fontSize: '11px',
                        fontWeight: '600'
                      }}
                    >
                      {priorityLabels[ticketDetails.priority]?.label}
                    </Badge>
                  </div>
                  <div className={`d-flex justify-content-between align-items-center ${styles.infoBox}`}>
                    <span className={styles.infoLabel}>Status:</span>
                    <Badge 
                      bg={statusLabels[ticketDetails.status]?.color}
                      className="px-2 py-1 mx-2"
                      style={{ fontSize: '11px', fontWeight: '600' }}
                    >
                      {statusLabels[ticketDetails.status]?.label}
                    </Badge>
                  </div>
                  <div className={`d-flex justify-content-between align-items-center ${styles.infoBox}`}>
                    <span className={styles.infoLabel}>Criado em:</span>
                    <span className={styles.infoValue}>{formatDate(ticketDetails.created_at)}</span>
                  </div>
                </div>
              </Drawer.Section>

              <Drawer.Divider />

              {/* Assunto */}
              <Drawer.Section title="Assunto" icon="mdi:text">
                <div className={styles.subjectBox}>
                  <strong>{ticketDetails.subject}</strong>
                </div>
              </Drawer.Section>

              <Drawer.Divider />

              {/* Histórico de Mensagens */}
              <Drawer.Section title="Histórico de Conversas" icon="mdi:message-text">
                {ticketDetails.messages && ticketDetails.messages.length > 0 ? (
                  <div className={styles.messageContainer}>
                    {ticketDetails.messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`${styles.messageBox} ${msg.is_admin_reply ? styles.messageBoxAdmin : styles.messageBoxUser}`}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Icon
                              icon={msg.is_admin_reply ? 'mdi:shield-account' : 'mdi:account'}
                              size={20}
                            />
                            <strong>{msg.user_name || 'Usuário'}</strong>
                            {msg.is_admin_reply && (
                              <Badge bg="primary">Admin</Badge>
                            )}
                          </div>
                          <small className="text-muted">{formatDate(msg.created_at)}</small>
                        </div>
                        <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                          {msg.message}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <Drawer.EmptyState
                    icon="mdi:message-outline"
                    text="Nenhuma mensagem no histórico"
                  />
                )}
              </Drawer.Section>

              <Drawer.Divider />

              {/* Formulário de Resposta */}
              {ticketDetails.status !== 'closed' && ticketDetails.status !== 'resolved' && (
                <Drawer.Section title="Responder ao Usuário" icon="mdi:reply">
                  <Form onSubmit={handleSendReply}>
                    <Form.Group className="mb-3">
                      <Form.Control
                        as="textarea"
                        rows={4}
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                        placeholder="Digite sua resposta..."
                        disabled={sending}
                      />
                    </Form.Group>
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={sending || !replyMessage.trim()}
                      className="w-100"
                    >
                      {sending ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Enviando...
                        </>
                      ) : (
                        <>
                          <Icon icon="mdi:send" className="me-2" />
                          Enviar Resposta
                        </>
                      )}
                    </Button>
                  </Form>
                </Drawer.Section>
              )}
            </>
          ) : (
            <Drawer.EmptyState
              icon="mdi:alert"
              text="Não foi possível carregar os detalhes do ticket."
            />
          )}
        </Drawer>
        </div>
      </div>
    </MasterLayout>
  );
};

export default AdminTicketsPage;

